import { useCallback, useState } from "react";
import HNotification from "../components/custom/notification/HNotification";
import { successNotifyWithDescription } from "../pages/utils/CommonNotifications";
import useHttp from "./useHttp";

/**
 * Fetches a single contact by contactId.
 * @param {Object} appProperties - App context (leftServiceId, etc.)
 * @returns {{ fetchContactById: (contactId: string) => Promise<Object|null> }}
 */
export default function useContacts(appProperties) {
  const { fetchData } = useHttp();
  const [loadingStates, setLoadingStates] = useState({});

  const updateConversationStatus = (contactId, status) => {
    return new Promise((resolve, reject) => {
      if (appProperties && contactId && status) {
        fetchData(`omessage/conversation/${contactId}/status`, "POST", { status }, appProperties)
          .then((response) => {
            if (response) {
              resolve(JSON.parse(response)?.data);
            } else {
              reject(new Error("Failed to update conversation status"));
            }
          })
          .catch((error) => {
            reject(error);
          });
      }
    });
  };

  const handleUpdateConversationStatus = (contactId, status, setNumbersList, setConversationContext) => {
    return new Promise((resolve, reject) => {
      if (setNumbersList && contactId && status && setConversationContext) {
        setLoadingStates((prev) => ({
          ...prev,
          [contactId]: {
            changeStatus: true,
          },
        }));
        updateConversationStatus(contactId, status)
          .then((updateConversationStatusResponse) => {
            if (updateConversationStatusResponse?.success) {
              const conversationStatus = Object.values(appProperties?.enums?.conversationStatus)?.find((item) => item?.code === status);
              const conversationStatusType = Object.values(appProperties?.enums?.conversationStatusType)?.find(
                (item) => item?.code === conversationStatus?.meta?.parent,
              );

              setConversationContext((prev) => ({
                ...prev,
                [contactId]: {
                  ...(prev?.[contactId] || {}),
                  conversationStatus: conversationStatus?.code,
                  conversationStatusType: conversationStatusType?.code,
                  conversations: [...(prev?.[contactId]?.conversations || []), ...(updateConversationStatusResponse?.data?.messages || [])],
                },
              }));
              if (setNumbersList) {
                setNumbersList((prevList) =>
                  prevList.map((item) =>
                    item.contactId === contactId
                      ? {
                          ...item,
                          conversationStatus: conversationStatus?.code,
                          conversationStatusType: conversationStatusType?.code,
                        }
                      : item,
                  ),
                );
              }
              successNotifyWithDescription("", "Conversation status updated successfully");
              resolve(updateConversationStatusResponse);
            } else {
              reject(new Error("Failed to update conversation status"));
            }
          })
          .catch((error) => {
            HNotification.notify({ type: "error", message: "Error", description: error?.message || "Failed to update conversation status" });
          })
          .finally(() => {
            setLoadingStates((prev) => ({
              ...prev,
              [contactId]: {
                changeStatus: false,
              },
            }));
          });
      } else {
        reject(new Error("Failed to update conversation status"));
      }
    });
  };

  const fetchContactById = useCallback(
    (contactId) => {
      return new Promise((resolve) => {
        if (!contactId || !appProperties?.leftServiceId) {
          resolve(null);
          return;
        }

        fetchData(`omessage/contact/${contactId}?leftServiceId=${appProperties?.leftServiceId}`, "GET", null, appProperties)
          .then((response) => {
            if (!response) {
              resolve(null);
              return;
            }
            const parsed = JSON.parse(response);
            if (parsed?.data?.success && parsed?.data?.contacts?.length > 0) {
              resolve(parsed.data.contacts[0]);
            } else {
              resolve(null);
            }
          })
          .catch(() => resolve(null));
      });
    },
    [fetchData, appProperties],
  );

  return { fetchContactById, handleUpdateConversationStatus, loadingStates };
}
